import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useStoreAnalytics } from "@/hooks/useStoreAnalytics";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatCurrency } from "@/lib/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { subDays, format } from "date-fns";
import {
  TrendingUp,
  TrendingDown,
  Package,
  Users,
  DollarSign,
  ShoppingCart,
  CheckCircle,
  XCircle,
} from "lucide-react";

const periods = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

export function QuickOverview() {
  const [selectedPeriod, setSelectedPeriod] = React.useState("7d");
  
  // Calculate date range based on selected period
  const memoizedEndDate = useMemo(() => new Date(), []);
  const memoizedStartDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - periods[selectedPeriod as keyof typeof periods]);
    return date;
  }, [selectedPeriod]);

  const { today, periodStats, analytics, popularProducts, isLoading } = useStoreAnalytics({
    startDate: memoizedStartDate,
    endDate: memoizedEndDate,
  });

  const stats = [
    {
      title: "Total Revenue",
      value: formatCurrency(periodStats?.totalRevenue || 0),
      icon: DollarSign,
      today: formatCurrency(today?.total_revenue || 0),
    },
    {
      title: "Total Orders",
      value: periodStats?.totalOrders || 0,
      icon: ShoppingCart,
      today: today?.total_orders || 0,
    },
    {
      title: "Completed Orders",
      value: periodStats?.completedOrders || 0,
      icon: CheckCircle,
      today: today?.completed_orders || 0,
    },
    {
      title: "Cancelled Orders",
      value: periodStats?.cancelledOrders || 0,
      icon: XCircle,
      today: today?.cancelled_orders || 0,
    },
    {
      title: "Active Products",
      value: periodStats?.totalProducts || 0,
      icon: Package,
      today: today?.total_products || 0,
    },
    {
      title: "New Customers",
      value: periodStats?.newCustomers || 0,
      icon: Users,
      today: today?.new_customers || 0,
    },
  ];

  const chartData = analytics?.map((day) => ({
    date: format(new Date(day.date), "MMM d"),
    revenue: day.total_revenue,
    orders: day.total_orders,
  })).reverse();

  if (isLoading) {
    return (
      <Card className="border-brand-primary/10">
        <CardHeader>
          <CardTitle className="text-brand-primary">Quick Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-brand-primary/10 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between border-b border-brand-primary/5 pb-4">
        <CardTitle className="text-brand-primary">Quick Overview</CardTitle>
        <Tabs value={selectedPeriod} onValueChange={setSelectedPeriod} className="h-8">
          <TabsList className="bg-[#967F81]/5">
            <TabsTrigger value="7d" className="data-[state=active]:bg-[#967F81] data-[state=active]:text-white">7D</TabsTrigger>
            <TabsTrigger value="30d" className="data-[state=active]:bg-[#967F81] data-[state=active]:text-white">30D</TabsTrigger>
            <TabsTrigger value="90d" className="data-[state=active]:bg-[#967F81] data-[state=active]:text-white">90D</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {stats.map((stat) => (
            <Card key={stat.title} className="border-brand-primary/10 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-brand-primary/70">
                      {stat.title}
                    </p>
                    <h3 className="text-2xl font-bold text-brand-primary">{stat.value}</h3>
                    <p className="text-xs text-brand-primary/60 mt-1">
                      Today: {stat.today}
                    </p>
                  </div>
                  <stat.icon className="h-6 w-6 text-brand-primary/70" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-8">
          <h4 className="text-sm font-medium mb-4 text-brand-primary/90">Revenue & Orders Trend</h4>
          <div className="h-[300px] bg-white p-4 rounded-lg border border-brand-primary/10">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="brand-primary20" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip 
                  contentStyle={{ 
                    borderColor: 'brand-primary20', 
                    borderRadius: '0.5rem', 
                    backgroundColor: 'white' 
                  }} 
                />
                <Bar
                  yAxisId="left"
                  dataKey="revenue"
                  fill="brand-primary"
                  name="Revenue"
                />
                <Bar
                  yAxisId="right"
                  dataKey="orders"
                  fill="brand-primary60"
                  name="Orders"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {popularProducts && popularProducts.length > 0 && (
          <div className="mt-8">
            <h4 className="text-sm font-medium mb-4 text-brand-primary/90">Popular Products</h4>
            <div className="space-y-3">
              {popularProducts.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between p-4 bg-white rounded-lg border border-brand-primary/10 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div>
                    <p className="font-medium text-brand-primary">{product.title}</p>
                    <p className="text-sm text-brand-primary/70">
                      {product.order_count} orders • {product.total_quantity} units
                    </p>
                  </div>
                  {product.total_quantity > (popularProducts[1]?.total_quantity || 0) ? (
                    <TrendingUp className="h-5 w-5 text-brand-primary" />
                  ) : (
                    <TrendingDown className="h-5 w-5 text-brand-primary/70" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
} 